import React from 'react';
import { Autocomplete, Checkbox, ListSubheader, TextField } from '@mui/material';

function normalizeOptions(options) {
  return (options ?? []).map((opt) => {
    if (opt != null && typeof opt === 'object' && 'value' in opt) {
      return {
        value: String(opt.value),
        label: opt.label != null && String(opt.label).trim() !== '' ? String(opt.label) : String(opt.value),
        group: opt.group != null && String(opt.group).trim() !== '' ? String(opt.group) : '',
      };
    }
    return { value: String(opt), label: String(opt), group: '' };
  });
}

/**
 * Dropdown that filters options as the user types.
 * `value` is a string (single) or string[] (multiple).
 */
export default function SearchableSelect({
  label,
  value,
  options = [],
  onChange,
  onBlur,
  name,
  multiple = false,
  error = false,
  helperText,
  disabled = false,
  required = false,
  size = 'medium',
  placeholder = 'Select…',
  allowEmpty = true,
  disableClearable,
  sx,
  fullWidth = true,
  hideLabel = false,
}) {
  const normalized = normalizeOptions(options);
  const hasGroups = normalized.some((opt) => opt.group);

  const selected = multiple
    ? normalized.filter((opt) => (Array.isArray(value) ? value : []).map(String).includes(opt.value))
    : normalized.find((opt) => String(value ?? '') === opt.value) ?? null;

  const emit = (next) => {
    if (!onChange) return;
    if (multiple) {
      onChange((next ?? []).map((opt) => opt.value));
      return;
    }
    onChange(next ? next.value : '');
  };

  return (
    <Autocomplete
      fullWidth={fullWidth}
      multiple={multiple}
      disableCloseOnSelect={multiple}
      options={normalized}
      value={selected}
      disabled={disabled}
      disableClearable={disableClearable ?? (!allowEmpty && !multiple)}
      autoHighlight
      autoSelect={!hasGroups}
      groupBy={hasGroups ? (opt) => opt.group || '' : undefined}
      renderGroup={
        hasGroups
          ? (params) => (
              <li key={params.key}>
                <ListSubheader
                  component="div"
                  sx={{ fontWeight: 700, lineHeight: '36px', bgcolor: 'background.paper' }}
                >
                  {params.group}
                </ListSubheader>
                <ul style={{ padding: 0 }}>{params.children}</ul>
              </li>
            )
          : undefined
      }
      onChange={(_, next) => emit(next)}
      onBlur={onBlur}
      getOptionLabel={(opt) => opt?.label ?? ''}
      isOptionEqualToValue={(a, b) => String(a?.value) === String(b?.value)}
      filterOptions={(opts, state) => {
        const q = String(state.inputValue || '').trim().toLowerCase();
        if (!q) return opts;
        return opts.filter(
          (opt) =>
            String(opt.label).toLowerCase().includes(q)
            || String(opt.value).toLowerCase().includes(q)
        );
      }}
      renderOption={(props, opt, { selected: isSelected }) => {
        const { key, ...liProps } = props;
        return (
          <li key={key ?? opt.value} {...liProps}>
            {multiple ? <Checkbox size="small" checked={isSelected} sx={{ mr: 1 }} /> : null}
            {opt.label}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label={hideLabel ? undefined : label}
          placeholder={placeholder}
          error={Boolean(error)}
          helperText={helperText || undefined}
          required={required}
          size={size}
        />
      )}
      sx={sx}
    />
  );
}
